import { useMemo } from 'react';
import MetricCard from '../shared/MetricCard';
import VendorTag from '../shared/VendorTag';
import DataTable from '../shared/DataTable';
import EmptyState from '../shared/EmptyState';
import { formatMoney } from '../../utils/formatters';

export default function CostDiscrepancies({ data }) {
  const items = data?.items || [];

  const totalVariance = data?.total_variance_usd || items.reduce((s, d) => s + Math.abs((d.po_cost_usd || 0) - (d.ro_cost_usd || 0)), 0);
  const overpaid = items.filter((d) => (d.po_cost_usd || d.po_cost || 0) > (d.ro_cost_usd || d.ro_cost || 0));

  const columns = useMemo(() => [
    {
      key: 'part_name',
      label: 'Part',
      render: (row) => (
        <div>
          <div className="font-bold text-sm">{row.part_name}</div>
          <div className="text-xs text-gray-400">{row.part_number}</div>
        </div>
      ),
    },
    {
      key: 'ro_number',
      label: 'RO / Customer',
      render: (row) => (
        <div>
          <div className="font-semibold text-sm">#{row.ro_number}</div>
          <div className="text-xs text-gray-400">{row.customer_name}</div>
        </div>
      ),
    },
    {
      key: 'vendor_name',
      label: 'Vendor',
      render: (row) => <VendorTag name={row.vendor_name} />,
    },
    {
      key: 'po_cost_usd',
      label: 'PO Cost',
      align: 'right',
      render: (row) => <span className="font-bold">{formatMoney(row.po_cost_usd)}</span>,
    },
    {
      key: 'ro_cost_usd',
      label: 'RO Cost',
      align: 'right',
      render: (row) => <span className="font-bold">{formatMoney(row.ro_cost_usd)}</span>,
    },
    {
      key: 'difference',
      label: 'Diff',
      align: 'right',
      accessor: (row) => (row.po_cost_usd || 0) - (row.ro_cost_usd || 0),
      render: (row) => {
        const diff = (row.po_cost_usd || 0) - (row.ro_cost_usd || 0);
        const isOver = diff > 0;
        return (
          <span
            className={`inline-block px-2.5 py-1 rounded-lg font-extrabold text-sm ${
              isOver ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
            }`}
          >
            {isOver ? '+' : ''}{formatMoney(diff)}
          </span>
        );
      },
    },
  ], []);

  return (
    <div>
      <div className="flex gap-3 mb-6 flex-wrap">
        <MetricCard
          label="Discrepancies Found"
          value={items.length.toString()}
          accent="#E65100"
          icon={'\u2696'}
        />
        <MetricCard
          label="Total Variance"
          value={formatMoney(totalVariance)}
          sub="Sum of all differences"
          accent="#C62828"
        />
        <MetricCard
          label="Overpaid (PO > RO)"
          value={overpaid.length.toString()}
          sub="Vendor charged more"
          accent="#D32F2F"
        />
      </div>

      <div className="text-xs text-gray-500 mb-4 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
        Compares the cost on the vendor's purchase order/invoice against the cost entered on the repair order. Differences may indicate pricing errors, unauthorized price changes, or data entry mistakes.
      </div>

      {items.length > 0 ? (
        <DataTable columns={columns} data={items} />
      ) : (
        <EmptyState text="No cost discrepancies found" />
      )}
    </div>
  );
}
